<script setup>
import { Link } from '@inertiajs/vue3'
import { Icon } from '@iconify/vue'

defineProps({
  variant: { type: String, default: 'yellow' },
  size: { type: String, default: 'md' },
  flat: { type: Boolean, default: false },
  block: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  icon: { type: String, default: null },
  iconEnd: { type: String, default: null },
  href: { type: String, default: null },
  tag: { type: String, default: null },
  method: { type: String, default: 'get' },
})
</script>

<template>
  <Component
    :is="tag ? tag : href ? Link : 'button'"
    :class="[
      'button',
      'font-mono uppercase',
      `button-${variant}`,
      `button-${size}`,
      { flat, block }
    ]"
    :href="href"
    :method="href ? method : null"
  >
    <span v-if="loading" class="with-icon flex items-center justify-center gap-2">
      <Icon icon="line-md:loading-loop" />
      <slot name="loading" />
    </span>
    <span v-else-if="icon || iconEnd" class="with-icon flex items-center justify-center gap-2">
      <Icon v-if="icon" :icon="icon" />
      <slot />
      <Icon v-if="iconEnd" :icon="iconEnd" />
    </span>
    <template v-else>
      <slot />
    </template>
  </Component>
</template>

<style lang="scss" scoped>
.button {
  display: inline-flex;
  justify-content: center;
  background: var(--btn-bg-color, var(--color-gray-200));
  color: var(--btn-text-color, var(--color-green-pine));
  padding: .5em .75em;
  font-size: var(--btn-text-size, var(--text-base));
  font-weight: bold;
  transition: .25s ease;
  text-decoration: none;

  &:hover:not(:disabled) {
    background: var(--btn-bg-color-hover, var(--btn-bg-color, var(--color-gray-200)));
    color: var(--btn-text-color-hover, var(--btn-text-color, var(--color-green-pine)));
  }

  &-lg {
    --btn-text-size: var(--text-md);
  }

  &-sm {
    --btn-text-size: var(--text-sm);
  }

  &-gray {
    --btn-bg-color-hover: var(--color-gray-300);
  }

  &-yellow {
    --btn-bg-color: var(--color-yellow);
    --focus-color: var(--color-black);

    &.flat:hover:not(:disabled) {
      --btn-bg-color: var(--color-orange);
    }
  }

  &-pink {
    --btn-bg-color: var(--color-pink);
    --focus-color: var(--color-black);
  }

  &-white {
    --btn-bg-color: var(--color-white);
  }

  &-green {
    --btn-bg-color: var(--color-green);
    --focus-color: var(--color-black);
    --btn-bg-color-hover: var(--color-green-dark);
    --btn-text-color-hover: var(--color-white);
  }

  &-red {
    --btn-bg-color: var(--color-red);
    --focus-color: var(--color-black);
    --btn-text-color: var(--color-white);
  }

  &-soft-red {
    --btn-bg-color: var(--color-gray-200);
    --btn-text-color: var(--color-red);
    --focus-color: var(--color-black);
    --btn-bg-color-hover: var(--color-red);
    --btn-text-color-hover: var(--color-white);
  }

  &-pine {
    --btn-bg-color: var(--color-green-pine);
    --btn-text-color: var(--color-white);
    --focus-color: var(--color-green-pine);

    &.flat:hover:not(:disabled) {
      --btn-text-color: var(--color-yellow);
    }
  }

  &-gray {
    --btn-bg-color: var(--color-gray-100);
    --btn-text-color: var(--color-gray-700);
    --focus-color: var(--color-gray-900);
    --btn-bg-color-hover: var(--color-gray-200);
    --btn-text-color-hover: var(--color-gray-900);
  }

  &-delete {
    --btn-bg-color: var(--color-gray-100);
    --btn-text-color: var(--color-gray-700);
    --focus-color: var(--color-gray-900);
    --btn-bg-color-hover: var(--color-gray-200);
    --btn-text-color-hover: var(--color-red);
  }

  &.block {
    width: 100%;
  }

  &:hover:not(.flat):not(:disabled) {
    box-shadow: .5em .5em 0 var(--color-green-pine);
    translate: -.5em -.5em;
  }

  &:active:not(.flat):not(:disabled) {
    box-shadow: .25em .25em 0 var(--color-green-pine);
    translate: -.25em -.25em;
  }

  &:disabled {
    opacity: .5;
  }
}
</style>